import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    serverTimestamp,
    DocumentData
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, getFunctionsInstance } from '@lib/firebase';

import { executeCloudFunction } from '@/shared/api/base/ServiceWrapper';
import ServiceBase from '@/shared/api/base/ServiceBase';

class CertificateService extends ServiceBase {
    constructor() {
        super('CertificateService');
    }

    /**
     * Get all certificates for a user
     */
    async getCertificatesByUserId(userId: string): Promise<any[]> {
        try {
            const certificatesRef = collection(db, 'users', userId, 'certificates');
            const querySnapshot = await getDocs(certificatesRef);
            const certificates: any[] = [];

            querySnapshot.forEach((docSnapshot) => {
                certificates.push({
                    id: docSnapshot.id,
                    ...docSnapshot.data()
                });
            });

            certificates.sort((a, b) => {
                const dateA = a.awardedAt?.toMillis?.() || 0;
                const dateB = b.awardedAt?.toMillis?.() || 0;
                return dateB - dateA;
            });

            return certificates;
        } catch (error) {
            this.logError(error, { method: 'getCertificatesByUserId', userId });
            throw error;
        }
    }

    /**
     * Get a specific certificate by ID
     */
    async getCertificateById(userId: string, certificateId: string): Promise<any> {
        try {
            const certificateRef = doc(db, 'users', userId, 'certificates', certificateId);
            const certificateDoc = await getDoc(certificateRef);

            if (!certificateDoc.exists()) {
                throw new Error('Certificate not found');
            }

            return {
                id: certificateDoc.id,
                ...certificateDoc.data()
            };
        } catch (error) {
            this.logError(error, { method: 'getCertificateById', userId, certificateId });
            throw error;
        }
    }

    /**
     * Generate an enrollment certificate using cloud function
     */
    async generateEnrollmentCertificate(userId: string, courseId: string, courseName: string): Promise<any> {
        return executeCloudFunction('generateEnrollmentCertificate', async () => {
            const functions = getFunctionsInstance();
            const generateCertFn = httpsCallable(functions, 'generateEnrollmentCertificate');

            try {
                const result = await generateCertFn({
                    userId,
                    courseId,
                    courseName
                });
                return result.data;
            } catch (error: any) {
                if (error.code === 'functions/not-found') {
                    throw new Error('Certificate generation function not deployed yet. Please try again later.');
                }
                throw error;
            }
        }, 'generateEnrollmentCertificate');
    }

    /**
     * Check eligibility for an enrollment certificate using cloud function
     */
    async checkEnrollmentCertificateEligibility(userId: string, courseId: string): Promise<any> {
        return executeCloudFunction('checkEnrollmentCertificateEligibility', async () => {
            const functions = getFunctionsInstance();
            const checkCertFn = httpsCallable(functions, 'checkEnrollmentCertificateEligibility');

            try {
                const result = await checkCertFn({
                    userId,
                    courseId
                });
                return result.data;
            } catch (error: any) {
                if (error.code === 'functions/not-found') {
                    throw new Error('Enrollment certificate eligibility check function not deployed yet. Please try again later.');
                }
                throw error;
            }
        }, 'checkEnrollmentCertificateEligibility');
    }

    /**
     * Get certificates by type for a user
     */
    async getCertificatesByType(userId: string, type: string): Promise<any[]> {
        try {
            const certificatesRef = collection(db, 'users', userId, 'certificates');
            const q = query(
                certificatesRef,
                where('type', '==', type)
            );

            const querySnapshot = await getDocs(q);
            const certificates: any[] = [];

            querySnapshot.forEach((docSnapshot) => {
                certificates.push({
                    id: docSnapshot.id,
                    ...docSnapshot.data()
                });
            });

            certificates.sort((a, b) => {
                const dateA = a.awardedAt?.toMillis?.() || 0;
                const dateB = b.awardedAt?.toMillis?.() || 0;
                return dateB - dateA;
            });

            return certificates;
        } catch (error) {
            this.logError(error, { method: 'getCertificatesByType', userId, type });
            throw error;
        }
    }

    /**
     * Check if user has an enrollment certificate for a course
     */
    async hasEnrollmentCertificate(userId: string, courseId: string): Promise<boolean> {
        try {
            const certificatesRef = collection(db, 'users', userId, 'certificates');
            const q = query(
                certificatesRef,
                where('courseId', '==', courseId),
                where('type', '==', 'enrollment')
            );

            const querySnapshot = await getDocs(q);
            return querySnapshot.size > 0;
        } catch (error) {
            this.logError(error, { method: 'hasEnrollmentCertificate', userId, courseId });
            throw error;
        }
    }

    /**
     * Mark a certificate as downloaded
     */
    async markCertificateAsDownloaded(userId: string, certificateId: string): Promise<void> {
        try {
            const certificateRef = doc(db, 'users', userId, 'certificates', certificateId);
            const certDoc = await getDoc(certificateRef);
            const currentData = certDoc.data();

            await updateDoc(certificateRef, {
                lastDownloadedAt: serverTimestamp(),
                downloadCount: (currentData?.downloadCount || 0) + 1
            });
        } catch (error) {
            this.logError(error, { method: 'markCertificateAsDownloaded', userId, certificateId });
            throw error;
        }
    }

    /**
     * Generate a completion certificate using cloud function
     */
    async generateCompletionCertificate(userId: string, courseId: string, courseName: string): Promise<any> {
        return executeCloudFunction('generateCompletionCertificate', async () => {
            const functions = getFunctionsInstance();
            const generateCompCertFn = httpsCallable(functions, 'generateCompletionCertificate');

            try {
                const result = await generateCompCertFn({
                    userId,
                    courseId,
                    courseName
                });
                return result.data;
            } catch (error: any) {
                if (error.code === 'functions/not-found') {
                    throw new Error('Completion certificate generation function not deployed yet. Please try again later.');
                }
                throw error;
            }
        }, 'generateCompletionCertificate');
    }

    /**
     * Check eligibility for a completion certificate using cloud function
     */
    async checkCompletionCertificateEligibility(userId: string): Promise<any> {
        return executeCloudFunction('checkCompletionCertificateEligibility', async () => {
            const functions = getFunctionsInstance();
            const checkCompCertFn = httpsCallable(functions, 'checkCompletionCertificateEligibility');

            try {
                const result = await checkCompCertFn({
                    userId
                });
                return result.data;
            } catch (error: any) {
                if (error.code === 'functions/not-found') {
                    throw new Error('Completion certificate eligibility check function not deployed yet. Please try again later.');
                }
                throw error;
            }
        }, 'checkCompletionCertificateEligibility');
    }
}

const certificateServices = new CertificateService();

// Maintain backward compatibility for named exports
export const getCertificatesByUserId = (userId: string) => certificateServices.getCertificatesByUserId(userId);
export const getCertificateById = (userId: string, certId: string) => certificateServices.getCertificateById(userId, certId);
export const generateEnrollmentCertificate = (...args: [string, string, string]) =>
    certificateServices.generateEnrollmentCertificate(...args);
export const getCertificatesByType = (userId: string, type: string) =>
    certificateServices.getCertificatesByType(userId, type);
export const hasEnrollmentCertificate = (userId: string, courseId: string) =>
    certificateServices.hasEnrollmentCertificate(userId, courseId);
export const markCertificateAsDownloaded = (userId: string, certId: string) =>
    certificateServices.markCertificateAsDownloaded(userId, certId);
export const generateCompletionCertificate = (...args: [string, string, string]) =>
    certificateServices.generateCompletionCertificate(...args);
export const checkCompletionCertificateEligibility = (userId: string) =>
    certificateServices.checkCompletionCertificateEligibility(userId);
export const checkEnrollmentCertificateEligibility = (userId: string, courseId: string) =>
    certificateServices.checkEnrollmentCertificateEligibility(userId, courseId);

export default certificateServices;
